import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  getAutor,
  getDraftPostById,
  getPostBySlug,
  getPostHomepage,
  getSingleOpinion,
} from 'api/services/wp';
import MySocialShare from 'modules/website/shared/components/MySocialShare';
import JuntsButton from 'modules/website/shared/components/JuntsButton';
import formatDate from 'utils/formatDate';
import htmlToPlain from 'utils/htmlToPlain';
import generateEmbeds from 'utils/generateEmbeds';
import htmlEntDecoder from 'utils/htmlEntDecoder';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import { Zoom } from 'react-awesome-reveal';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { componentModal } from 'utils/modal';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import YoutubeEmbed from 'modules/website/shared/components/YoutubeEmbed';
import extractYoutubeId from 'utils/extractYoutubeId';
import FlickrEmbed from 'modules/website/shared/components/FlickEmbed';
import { featuredImageFallBack } from 'modules/website/shared/constants';
import useVideoProportionCalculator from 'modules/website/hooks/useVideoProportionCalculator';
import LastNewCard from './components/LastNewCard';
import './SingleNoticia.scss';

const propTypes = { isOpinion: PropTypes.bool, isDraft: PropTypes.bool };
const defaultProps = { isOpinion: false, isDraft: false };

function SingleNoticia({ isOpinion, isDraft }) {
  // Hooks
  const { slug } = useParams();
  const navigate = useNavigate();
  const { width, height } = useVideoProportionCalculator();

  // States
  const [noticia, setNoticia] = useState(null);
  const [tags, setTags] = useState(null);
  const [featuredImage, setFeaturedImage] = useState(null);
  const [lastNoticies, setLastNoticies] = useState(null);
  const [autor, setAutor] = useState(null);

  // Effects
  useEffect(() => {
    window.scrollTo(0, 0);

    async function loadNoticia() {
      if (isOpinion) {
        getSingleOpinion(slug)
          .then(({ data: fetchedOpinion }) => {
            setNoticia(fetchedOpinion[0]);
            getAutor(fetchedOpinion[0].autor[0])
              .then(({ data: fetchedAutor }) => setAutor(fetchedAutor))
              .catch((error) => console.error(error));
          })
          .catch((error) => console.error(error));
      } else if (isDraft) {
        let id = slug;

        if (containsLetters(slug)) {
          const { data: response } = await getPostBySlug(slug);
          id = response[0].id;
        }

        getDraftPostById(id).then(({ response: data }) => {
          setNoticia(data[0]);
          const fetchedTags = data?._embedded?.['wp:term']?.[1] || [];
          if (fetchedTags.length > 0) {
            setTags(tags);
          } else {
            setTags(null);
          }
          setFeaturedImage(
            data?._embedded?.['wp:featuredmedia']?.[0]?.source_url || featuredImageFallBack
          );
        });
      } else {
        getPostBySlug(slug).then(({ data }) => {
          setNoticia(data[0]);

          const fetchedTags = data?.[0]?._embedded?.['wp:term']?.[1] || [];
          if (fetchedTags.length > 0) {
            setTags(tags);
          } else {
            setTags(null);
          }

          // featured image
          setFeaturedImage(
            data[0]?._embedded?.['wp:featuredmedia']?.[0]?.source_url || featuredImageFallBack
          );
        });
      }
    }

    loadNoticia();

    // Load last news
    getPostHomepage().then(({ data }) => setLastNoticies(data.slice(0, 3)));
  }, [slug, isOpinion]);

  useEffect(() => {
    if (window.twttr) {
      window.twttr.widgets.load();
    }
  }, [noticia]);

  // Functions
  function containsLetters(str) {
    return /[a-zA-Z]/.test(str);
  }

  function goBack() {
    navigate(isOpinion ? '/opinio' : '/actualitat', { replace: true });
  }

  function openVideo() {
    componentModal(
      <Zoom>
        <YoutubeEmbed
          id={extractYoutubeId(noticia.acf.video_de_la_roda_de_premsa)}
          height={String(height)}
        />
      </Zoom>,
      `${width}px`,
      'black'
    );
  }

  function openFlickr() {
    componentModal(
      <Zoom>
        <FlickrEmbed html={noticia.acf.fotografies_de_la_roda_de_premsa} />
      </Zoom>,
      '60%',
      'black'
    );
  }

  // Rendering
  return (
    <div className="singlenoticia__container__flex flex-center">
      <div className="singlenoticia__container">
        {noticia && (
          <div className="singlenoticia__main">
            {isOpinion ? (
              <div className="singlenoticia__opinion__header">
                {autor && (
                  <>
                    <div className="singlenoticia__opinion__photo">
                      <img src={autor.acf.fotografia} alt="" />
                    </div>

                    <div className="singlenoticia__opinion__group">
                      <div className="singlenoticia__opinion__date">
                        <h2>{formatDate(noticia.date)}</h2>
                      </div>

                      <div className="singlenoticia__opinion__carrec">
                        <h2>{autor.acf.carrec}</h2>
                      </div>

                      <div className="singlenoticia__opinion__nom">
                        <h2>{autor.name}</h2>
                      </div>
                    </div>

                    <div className="singlenoticia__opinion__share">
                      <MySocialShare
                        title={htmlEntDecoder(noticia.title.rendered)}
                        description={htmlToPlain(noticia.excerpt.rendered)}
                      />
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="singlenoticia__header">
                <div className="singlenoticia__tags__date">
                  <ul>{tags && tags.map((e) => <li key={e.name}>{e.name}</li>)}</ul>
                  <h2>{formatDate(noticia.date)}</h2>
                </div>

                <div className="singlenoticia__share">
                  <MySocialShare
                    title={htmlEntDecoder(noticia.title.rendered)}
                    description={htmlToPlain(noticia.excerpt.rendered)}
                  />
                </div>
              </div>
            )}

            <div className="singlenoticia__content">
              <div className="singlenoticia__content--title">
                <h1>{htmlEntDecoder(noticia.title.rendered)}</h1>
              </div>

              <div className="singlenoticia__content--image">
                <img src={featuredImage} alt="" />
              </div>

              <div className="singlenoticia__content--main">
                <div
                  className="content"
                  dangerouslySetInnerHTML={{
                    __html: generateEmbeds(noticia.content.rendered),
                  }}
                />
              </div>

              <div className="singlenoticia__content__resources ">
                {noticia.acf.materials && (
                  <a href={noticia.acf.nota_de_premsa} target="_blank" rel="noreferrer">
                    <div className="singlenoticia__content__resources--nota bg-second-color">
                      <SystemUpdateAltIcon className="singlenoticia__content__resources-icon" />
                      <div className="singlenoticia__content__resources--nota--text">
                        <h2>Materials</h2>
                        <h3>Descarrega els materials</h3>
                      </div>
                    </div>
                  </a>
                )}

                {noticia.acf.nota_de_premsa && (
                  <a href={noticia.acf.nota_de_premsa} target="_blank" rel="noreferrer">
                    <div className="singlenoticia__content__resources--nota">
                      <SystemUpdateAltIcon className="singlenoticia__content__resources-icon" />
                      <div className="singlenoticia__content__resources--nota--text">
                        <h2>Nota de Premsa</h2>
                        <h3>Descarrega el PDF</h3>
                      </div>
                    </div>
                  </a>
                )}

                {noticia.acf.fotografies_de_la_roda_de_premsa && (
                  <div className="singlenoticia__content__resources--images">
                    <div className="singlenoticia__content__resources--images-title ">
                      <button
                        type="button"
                        className="btn-chromeless d-flex flex-align-items-center flex-gap-md"
                        onClick={openFlickr}
                      >
                        <CameraAltIcon className="singlenoticia__content__resources-icon" />
                        <h2>Fotografies de la roda de premsa</h2>
                      </button>
                    </div>
                  </div>
                )}

                {noticia.acf.video_de_la_roda_de_premsa && (
                  <div className="singlenoticia__content__resources--videos">
                    <div className="singlenoticia__content__resources--videos-title ">
                      <button
                        type="button"
                        className="btn-chromeless d-flex flex-align-items-center flex-gap-md"
                        onClick={openVideo}
                      >
                        <PlayCircleOutlineIcon className="singlenoticia__content__resources-icon" />
                        <h2>Video de la roda de premsa</h2>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div className="button__back" onClick={() => goBack()} onKeyDown={() => goBack()}>
              <ArrowBackIcon className="button__back--icon" />
              <h2>Torna</h2>
            </div>
          </div>
        )}

        <aside>
          <div className="singlenoticia__lastnews">
            <div className="singlenoticia__lastnews--title">
              <h2 style={{ fontSize: '1.5rem' }}>Últimes noticies</h2>
            </div>
            {lastNoticies && (
              <div className="singlenoticia__lastnews__cards">
                {lastNoticies.map((lastNoticia) => (
                  <LastNewCard
                    date={formatDate(lastNoticia.date)}
                    title={lastNoticia.title.rendered}
                    slug={lastNoticia.slug}
                    key={lastNoticia.slug}
                  />
                ))}
              </div>
            )}
            <div className="singlenoticia__lastnews__button">
              <Link to="/actualitat">
                <JuntsButton>
                  Tota l&apos;actualitat <ArrowForwardIcon />
                </JuntsButton>
              </Link>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

SingleNoticia.propTypes = propTypes;
SingleNoticia.defaultProps = defaultProps;

export default SingleNoticia;
